// Pre-fills assignments for AM-shift drivers so the grid looks lived-in.
//
// Rules:
//   - Demo "now" is noon. Each driver gets 2 runs (3 if shift ends
//     within ~2 hours of noon — they're closest to clocking out, so
//     denser plan).
//   - Runs use the SOURCE FLIGHT'S REAL ETA from the BANK. No
//     fabricated times — the grid and timeline show the assignment
//     exactly where the inbound actually lands.
//   - Pool: ASSIGNABLE_RUNS whose inbound ETA sits inside the driver's
//     shift window (start + 15 min, end − 30 min).
//   - No duplicate inbounds per driver.
//   - No two picks closer than 35 min apart — a driver can't be at
//     two gates simultaneously. Resulting gaps naturally fall across
//     the grid's red / yellow / green color buckets.

import { ROSTER } from './mockRoster';
import { ASSIGNABLE_RUNS, RUNS_BY_ID } from './mockBank';
import { SEED_TEAMS } from './mockTeams';

// Demo wall-clock time, in minutes since midnight.
export const DEMO_NOW_MIN = 12 * 60; // 12:00 noon

// Decode a shift code like "5-13b" → { startMin, endMin }
//   number-number = start hour, end hour
//   suffix a/b/c  = :15 / :30 / :45 minute offset
export function parseShiftCode(code) {
  if (!code) return null;
  const m = String(code).match(/^(\d{1,2})-(\d{1,2})([abc])?$/i);
  if (!m) return null;
  const startHr = parseInt(m[1], 10);
  let endHr     = parseInt(m[2], 10);
  // Wrap overnight (e.g. 17-01a means 17:15 → next-day 01:15)
  if (endHr <= startHr) endHr += 24;
  const minuteOffset = m[3] === 'b' ? 30 : m[3] === 'c' ? 45 : 15;
  return {
    startMin: startHr * 60 + minuteOffset,
    endMin:   endHr   * 60 + minuteOffset,
  };
}

function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function pad(n) { return n < 10 ? `0${n}` : `${n}`; }
function fmtTime(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${pad(h)}:${pad(m)}`;
}

const MIN_INTER_RUN_GAP = 35;    // a driver can't be at two gates closer than this
const SHIFT_START_BUFFER = 15;   // grace after clock-in before first run
const SHIFT_END_BUFFER   = 30;   // need at least this much shift left to take a run

// Pick up to `count` runs from `pool` for a driver/team whose shift
// covers [shiftStart, shiftEnd]. Uses the actual flight ETAs (no
// fabrication), guarantees no duplicate inbounds, and prevents
// scheduling a single driver at two flights closer than MIN_INTER_RUN_GAP.
function pickRunsForShift(rng, shiftStart, shiftEnd, count, pool) {
  // Eligible pool — inbound ETA inside the shift window with buffers.
  const window = pool.filter(run =>
    run.inboundEtaMins >= shiftStart + SHIFT_START_BUFFER &&
    run.inboundEtaMins <= shiftEnd - SHIFT_END_BUFFER
  );
  if (window.length === 0) return [];

  // Fisher–Yates shuffle so different drivers get different runs even
  // though they share the same pool.
  const shuffled = window.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const picks = [];
  const usedInbounds = new Set();
  for (const run of shuffled) {
    if (picks.length >= count) break;
    if (usedInbounds.has(run.inboundId)) continue;
    if (picks.some(p => Math.abs(p.inboundEtaMins - run.inboundEtaMins) < MIN_INTER_RUN_GAP)) continue;
    picks.push(run);
    usedInbounds.add(run.inboundId);
  }

  // Order by ETA so slot 0 is the earliest assignment of the day.
  picks.sort((a, b) => a.inboundEtaMins - b.inboundEtaMins);
  return picks;
}

// Build assignments. Two passes:
//   1. Teams — every driver on a team gets the SAME flights at the SAME
//      real ETAs. They differ only by slotLabel ("Drv 1", "Drv 2", …)
//      since they're each working a different role on the same inbound.
//   2. Solo drivers (not on any team) — get their own picks.
function buildSeedAssignments() {
  const r = rng(7);
  const byDriver = new Map();

  // driverId → true if member of any team (skipped in the solo pass)
  const teamDriverIds = new Set();
  SEED_TEAMS.forEach(t => t.driverIds.forEach(id => teamDriverIds.add(id)));

  // ── Pass 1: per-team shared schedule ──────────────────────────────
  SEED_TEAMS.forEach(team => {
    const members = team.driverIds
      .map(id => ROSTER.AM.find(d => d.id === id))
      .filter(Boolean);
    if (members.length === 0) return;

    // Use the LATEST start and EARLIEST end so the shared schedule
    // fits inside every team member's shift.
    const shifts = members.map(m => parseShiftCode(m.shiftCode)).filter(Boolean);
    if (shifts.length === 0) return;
    const teamStart = Math.max(...shifts.map(s => s.startMin));
    const teamEnd   = Math.min(...shifts.map(s => s.endMin));

    const minsUntilOff = teamEnd - DEMO_NOW_MIN;
    const count = (minsUntilOff <= 120) ? 3 : 2;

    const picks = pickRunsForShift(r, teamStart, teamEnd, count, ASSIGNABLE_RUNS);

    // Same flights + REAL ETAs for every team member; each gets a
    // unique slotLabel so the role differs (Drv 1, Drv 2, …).
    const teamRuns = picks.map((run, i) => ({
      slot: i,
      runId: run.id,
      etaMins: run.inboundEtaMins,
      eta: run.inboundEta,
    }));
    members.forEach((member, idx) => {
      byDriver.set(member.id, teamRuns.map(run => ({
        ...run,
        slotLabel: `Drv ${idx + 1}`,
      })));
    });
  });

  // ── Pass 2: solo drivers ──────────────────────────────────────────
  ROSTER.AM.forEach(driver => {
    if (teamDriverIds.has(driver.id)) return;
    const shift = parseShiftCode(driver.shiftCode);
    if (!shift) return;

    const minsUntilOff = shift.endMin - DEMO_NOW_MIN;
    const count = (minsUntilOff <= 120) ? 3 : 2;

    const picks = pickRunsForShift(r, shift.startMin, shift.endMin, count, ASSIGNABLE_RUNS);
    const runs = picks.map((run, i) => ({
      slot: i,
      runId: run.id,
      etaMins: run.inboundEtaMins,
      eta: run.inboundEta,
    }));
    byDriver.set(driver.id, runs);
  });

  return byDriver;
}

export const SEED_ASSIGNMENTS = buildSeedAssignments();

// Helper: returns 8-element array for a driver, with run or null per slot.
// The run object's inboundEta/inboundEtaMins are overridden to the
// schedule-specific time so the grid's gap calculations work correctly.
export function slotsForDriver(driverId, assignmentMap = SEED_ASSIGNMENTS) {
  const slots = new Array(8).fill(null);
  const list = assignmentMap.get(driverId) || [];
  list.forEach(({ slot, runId, eta, etaMins, slotLabel }) => {
    const run = RUNS_BY_ID.get(runId);
    if (run) {
      slots[slot] = {
        ...run,
        slotLabel:      slotLabel ?? run.slotLabel,
        inboundEta:     eta       ?? run.inboundEta,
        inboundEtaMins: etaMins   ?? run.inboundEtaMins,
      };
    }
  });
  return slots;
}
